import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { get_cookie } from "utils";
import { getMinBookableHour, isTimeBookable } from "utils/reservation";
import { apiDateFromDisplay, resolveTimeSelection, selectableHours } from "utils/flexible-availability-rules";


function TimePicker({
  defaultValue,
  parentTimeChangeHandler,
  currentObjName,
  selectedDate,
  setStartTimeDefaultValue,
  setEndTimeDefaultValue,
  vehicleSlug,
  selectedStartDate,
  selectedEndDate,
  startTimeDefaultValue,
  endTimeDefaultValue,
  setNoSlotError,
  setIsReserveEnable,
  isVehicleEdit,
  isShowCalendar,
  onUnavailableValue,
  flexibleAvailability = null,
  // Optional. When passed (null while the vehicle is still loading), only
  // availability fetched for this vehicle is used; anything else in the
  // shared store - e.g. the previously viewed vehicle's slots - is treated
  // as not loaded yet. Omit it to keep the store-wide behaviour.
  vehicleId
}) {
  // State management
  const [filteredTimes, setFilteredTimes] = useState([]);
  const [selectedValue, setSelectedValue] = useState(defaultValue || "");

  // Refs for tracking previous values to optimize re-renders
  const hasInitialized = useRef(false);
  const prevStartDate = useRef();
  const prevEndDate = useRef();
  const prevStartTime = useRef();
  const prevAvailableDates = useRef();
  const prevSlotVehicleId = useRef();
  const prevFlexibleAvailability = useRef();

  // Redux state
  const { vehicleSlot } = useSelector(({ vehicle }) => vehicle);

  // vehicleSlot is shared by every vehicle page and is not reset on
  // client-side navigation, so it can still hold another vehicle's data.
  const isVehicleScoped = vehicleId !== undefined;
  const slotVehicleId = vehicleSlot?.vehicle_id == null ? null : String(vehicleSlot.vehicle_id);
  const isSlotUnresolved =
    isVehicleScoped && (vehicleId === null || slotVehicleId !== String(vehicleId));
  // Drive Now lead time from the API: no start before it, for this vehicle only.
  const earliestStart = isSlotUnresolved ? null : vehicleSlot?.earliest_start || null;

  // Constants
  const PREFERRED_DEFAULT_TIMES = ["10:00", "9:00", "8:00"];

  // Memoized values
  const startTimeHour = useMemo(() => {
    if (!startTimeDefaultValue) return null;
    const hour = parseInt(startTimeDefaultValue.split(":")[0], 10);
    return isNaN(hour) ? null : hour;
  }, [startTimeDefaultValue]);

  /**
   * Generates filtered time options based on availability and constraints
   * @param {string} date - Selected date
   * @param {Object} availableDates - Available date slots
   * @returns {Array} Filtered time options
   */
  const getFilteredTimeOptions = useCallback((date, availableDates) => {
    if (!date || !availableDates) return [];

    // Evaluated on every call (not memoized at mount) so a page left open
    // doesn't keep offering slots that have since moved into the past.
    const isSameStartEndDate = selectedStartDate === selectedEndDate;

    return selectableHours(date, availableDates, {
      minBookableHour: getMinBookableHour(moment(date, "MMM DD, YYYY").format("YYYY-MM-DD"), earliestStart),
      afterHour: isSameStartEndDate && startTimeHour ? startTimeHour : null,
      flexibleAvailability,
      endpoint: currentObjName === "endDateTime" ? "end" : "start",
    });
  }, [selectedStartDate, selectedEndDate, startTimeHour, earliestStart, flexibleAvailability, currentObjName]);

  /**
   * Updates error states based on available times
   */
  const updateErrorStates = useCallback((times) => {
    const hasSlots = times.length > 0;

    if (setNoSlotError) {
      setNoSlotError(hasSlots ? "" : "No slots available for this date. Please select a different end date.");
    }

    if (setIsReserveEnable) {
      setIsReserveEnable(!hasSlots);
    }
  }, [setNoSlotError, setIsReserveEnable]);

  /**
   * Sets default time values when no explicit values are provided or when current values are invalid
   */
  const setDefaultTimeValues = useCallback((times) => {
    if (!times.length) return;

    const hasTimeCookie = Boolean(get_cookie("timerange"));
    const defaultTime = PREFERRED_DEFAULT_TIMES.find(time => times.includes(time)) || times[0];
    // A time carried over in the timerange cookie is normally kept as-is,
    // but never when it is already in the past for the selected date.
    const isPastTime = (time) => Boolean(time) && !isTimeBookable(selectedDate, time, earliestStart);
    const needsDefault = (time) =>
      hasTimeCookie ? isPastTime(time) : (!time || !times.includes(time));

    // Set start time default if not set or not available in current times
    if (setStartTimeDefaultValue && needsDefault(startTimeDefaultValue)) {
      setStartTimeDefaultValue(defaultTime);
    }

    // Set end time default if not set or not available in current times (but only if not in edit mode)
    if (setEndTimeDefaultValue && !isVehicleEdit && needsDefault(endTimeDefaultValue)) {
      setEndTimeDefaultValue(defaultTime);
    }
  }, [
    selectedDate,
    earliestStart,
    isVehicleEdit,
    startTimeDefaultValue,
    endTimeDefaultValue,
    setStartTimeDefaultValue,
    setEndTimeDefaultValue
  ]);

  // Effect for updating filtered times
  useEffect(() => {
    // Range checks made on submit never drive the options.
    if (vehicleSlot?.event === "submit") return;

    // This vehicle's availability hasn't arrived yet: offer nothing rather
    // than another vehicle's hours, and don't judge the current value
    // against them (resolveTimeSelection only rejects against real options).
    if (isSlotUnresolved) {
      setFilteredTimes((times) => (times.length ? [] : times));
      prevSlotVehicleId.current = undefined;
      prevAvailableDates.current = undefined;
      return;
    }

    const availableDates = vehicleSlot?.available_dates;
    if (!selectedDate || !availableDates) return;

    const startTimeChanged = prevStartTime.current !== startTimeHour;
    const startDateChanged = prevStartDate.current !== selectedStartDate;
    const endDateChanged = prevEndDate.current !== selectedEndDate;
    const hasCookie = Boolean(get_cookie("timerange"));
    // Fresh availability must be applied even when dates and times stay the
    // same (another vehicle, or a later response for this one). While the
    // calendar is open the store follows the months being browsed, so a
    // response that doesn't cover the selected date is not applied then.
    const vehicleChanged = isVehicleScoped && prevSlotVehicleId.current !== slotVehicleId;
    const apiDate = apiDateFromDisplay(selectedDate);
    const coversSelectedDate = Boolean(apiDate && availableDates[apiDate]);
    const slotChanged =
      prevAvailableDates.current !== availableDates && !isShowCalendar && coversSelectedDate;

    const shouldUpdateTimes =
      prevFlexibleAvailability.current !== flexibleAvailability ||
      vehicleChanged ||
      slotChanged ||
      (!isShowCalendar && startTimeChanged) ||
      (hasCookie && !hasInitialized.current) ||
      startDateChanged ||
      endDateChanged;

    if (!shouldUpdateTimes) return;

    const times = getFilteredTimeOptions(selectedDate, availableDates);

    setFilteredTimes(times);
    updateErrorStates(times);

    // Update ref values
    prevStartDate.current = selectedStartDate;
    prevEndDate.current = selectedEndDate;
    prevStartTime.current = startTimeHour;
    prevAvailableDates.current = availableDates;
    prevSlotVehicleId.current = slotVehicleId;
    prevFlexibleAvailability.current = flexibleAvailability;

    if (!hasInitialized.current) {
      hasInitialized.current = true;
    }
  }, [
    selectedDate,
    vehicleSlot,
    slotVehicleId,
    isVehicleScoped,
    isSlotUnresolved,
    flexibleAvailability,
    startTimeHour,
    selectedStartDate,
    selectedEndDate,
    isShowCalendar,
    getFilteredTimeOptions,
    updateErrorStates
  ]);

  // Effect for setting default values
  useEffect(() => {
    if (!selectedDate || !filteredTimes.length) {
      if (!get_cookie("timerange") && setEndTimeDefaultValue && !vehicleSlug) {
        setEndTimeDefaultValue("");
      }
      return;
    }

    // Check if current end time default value is still available in filtered times
    const isCurrentEndTimeAvailable = endTimeDefaultValue && filteredTimes.includes(endTimeDefaultValue);

    // If end time is not available or not set, update it
    const isCurrentEndTimePast = endTimeDefaultValue && !isTimeBookable(selectedDate, endTimeDefaultValue, earliestStart);
    if (
      setEndTimeDefaultValue &&
      ((!isCurrentEndTimeAvailable && !get_cookie("timerange")) || isCurrentEndTimePast)
    ) {
      const defaultTime = PREFERRED_DEFAULT_TIMES.find(time => filteredTimes.includes(time)) || filteredTimes[0];
      setEndTimeDefaultValue(defaultTime);
    }

    // Set initial default values if none exist
    setDefaultTimeValues(filteredTimes);
  }, [selectedDate, filteredTimes, endTimeDefaultValue, earliestStart, setDefaultTimeValues, vehicleSlug, setEndTimeDefaultValue]);

  // Effect for syncing with defaultValue prop
  useEffect(() => {
    setSelectedValue(defaultValue || "");
  }, [defaultValue]);

  const { selectValue, isUnavailable } = useMemo(
    () => resolveTimeSelection(selectedValue, filteredTimes),
    [selectedValue, filteredTimes]
  );

  // A value that isn't among the options (e.g. carried in from a cookie) is
  // never displayed as the first option: the select shows a placeholder and
  // the value is handed back so the page clears it from state and pricing.
  useEffect(() => {
    if (!isUnavailable) return;
    if (onUnavailableValue) {
      onUnavailableValue(currentObjName, selectedValue);
    } else if (currentObjName === "endDateTime" && setEndTimeDefaultValue) {
      setEndTimeDefaultValue("");
    } else if (currentObjName !== "endDateTime" && setStartTimeDefaultValue) {
      setStartTimeDefaultValue("");
    }
  }, [isUnavailable, selectedValue, currentObjName, onUnavailableValue, setEndTimeDefaultValue, setStartTimeDefaultValue]);

  /**
   * Handles time selection changes
   */
  const handleTimeChange = useCallback((event) => {
    const value = event.target.value;
    setSelectedValue(value);

    if (parentTimeChangeHandler) {
      parentTimeChangeHandler(value, currentObjName);
    }
  }, [parentTimeChangeHandler, currentObjName]);

  /**
   * Formats time for display in 12-hour format
   */
  const formatTimeForDisplay = useCallback((time, date) => {
    const formattedDate = moment(date, "MMM DD, YYYY").format("YYYY-MM-DD");
    const timeString = time?.padStart(5, "0");

    if (!timeString || !moment(timeString, "HH:mm", true).isValid()) {
      return null;
    }

    const dateTimeString = `${formattedDate} ${timeString}`;
    return moment(dateTimeString, "YYYY-MM-DD HH:mm").format("h:mm A");
  }, []);

  // Memoized time options for rendering
  const timeOptions = useMemo(() => {
    if (!filteredTimes.length || !selectedDate) return [];

    return filteredTimes
      .map((time, index) => {
        const formattedTime = formatTimeForDisplay(time, selectedDate);
        return formattedTime ? { value: time, label: formattedTime, key: index } : null;
      })
      .filter(Boolean);
  }, [filteredTimes, selectedDate, formatTimeForDisplay]);

  // Determine CSS classes
  const formGroupClasses = useMemo(() => {
    const baseClasses = "floating-form-group select-option";
    const hasValue = defaultValue || selectedValue || selectedDate;
    return hasValue ? `${baseClasses} value-selected` : baseClasses;
  }, [defaultValue, selectedValue, selectedDate]);

  return (
    <Form.Group controlId="time.ControlSelect" className={formGroupClasses}>
      <Form.Control
        as="select"
        value={selectValue}
        className="floating-input"
        onChange={handleTimeChange}
      >
        {selectValue === "" && timeOptions.length > 0 && (
          <option value="" disabled>
            Select time
          </option>
        )}
        {timeOptions.map(({ value, label, key }) => (
          <option value={value} key={key}>
            {label}
          </option>
        ))}
      </Form.Control>
      <Form.Label className="floating-label">Time*</Form.Label>
    </Form.Group>
  );
}

export default React.memo(TimePicker);
